import { useState } from "react";
import useConfirmationModal from "../../../components/Modals/ConfirmationModal/useConfirmationModal";
import { useTranslation } from "react-i18next";
import InventoryOperationService from "../../../api/Inventory/InventoryOperationService";
import { ErrorMessage, Success } from "../../../components/Notifications/Notifications";
import { markPendingSuccessNotice } from "../../../components/Notifications/pendingSuccessNotice";
import { DestinationType, MoveStatus, MoveTypes } from "../../../api/Inventory/Entities/InventoryMovementEntities";

export default function useOperationConfirmation() {
    const [modal, modalActions] = useConfirmationModal()
    const { t } = useTranslation();
    const [isWorking, setIsWorking] = useState(false)

    const saveDispenseAsPeding = async ({ patient, destinationType, patientVisitId, move, succeededCallback }) => {
        await confirmOperation({
            patient,
            destinationType,
            patientVisitId,
            submitHandler: InventoryOperationService.SavePending,
            move,
            succeededCallback
        })
    }

    const confirmDispense = async ({ patient, destinationType, patientVisitId, move, succeededCallback }) => {
        await confirmOperation({
            patient,
            destinationType,
            patientVisitId,
            submitHandler: InventoryOperationService.ExcecuteOperation,
            move,
            succeededCallback
        })
    }

    const confirmOperation = async ({ patient, destinationType, patientVisitId, move, submitHandler, succeededCallback }) => {

        //Valido si el paciene es null solo si el destino ya no fue seteado.
        if (move.destination == null && (patient == null || patient.medicalRecordNumber == null || patient.medicalRecordNumber.trim() == '')) {
            ErrorMessage(t('inventory.dispense.selectAPatient'))
            return;
        }

        const isNewMove = move.id < 1;

        await modalActions.Show({
            title: t('inventory.movements.confirmations.completeTitle'),
            textContent: t('inventory.movements.confirmations.completeTextContent'),
            buttonText: t('buttons.confirm'),
            useComment: true,
            onConfirmation: async ({ comment }) => {
                if (isWorking == true) return;

                const isCommentEmpty = comment == null || comment.trim() == '';

                const isPharmacyRequest = move.moveType?.toLowerCase() == MoveTypes.PharmacyDispenseRequest.Name;
                if (isNewMove && !isPharmacyRequest || (isPharmacyRequest && move.status == MoveStatus.Pending.Name)) {
                    const isLaboratoryKit = move.moveType?.toLowerCase() === MoveTypes.LaboratoryKitDispense.Name.toLowerCase();
                    const expirationErrorKey = getExpirationErrorKey(move.items, isCommentEmpty, isLaboratoryKit);

                    if (expirationErrorKey) {
                        ErrorMessage(t(expirationErrorKey));
                        return;
                    }
                }

                try {
                    const destination = move.destination == null
                        ? createDestination({ patient, destinationType, patientVisitId })
                        : move.destnation

                    setIsWorking(true)
                    const response = await submitHandler({
                        ...move,
                        destination: destination,
                        comment: comment
                    })
                    if (response.Succeeded) {
                        markPendingSuccessNotice("commons.successSave")
                        succeededCallback()
                    }
                    else
                        ErrorMessage("errors.serverError")
                } catch (e) {
                    console.error(e)
                    ErrorMessage("errors.serverError")

                } finally {
                    setIsWorking(false)
                }
            }
        })
    }

    const createDestination = ({ patient, destinationType, patientVisitId }) => {
        switch (destinationType) {
            case DestinationType.PharmacyPatient:
                return { destinationType: DestinationType.PharmacyPatient.Name, destinationId: patient.medicalRecordNumber }
            case DestinationType.ProtocolPatient:
                if (patientVisitId == null) {
                    throw new Error("Patient Visit is required for ProtocolPatient destination type");
                }
                return { destinationType: DestinationType.ProtocolPatient.Name, destinationId: patient.medicalRecordNumber, patientVisitId: patientVisitId }
            default:
                throw new Error("Invalid destination type provided");
        }
    }

    const cancelDispenseOperation = async ({ move, succeededCallback }) => {

        await modalActions.Show({
            title: t("inventory.commons.cancelOperationTitle"),
            textContent: t('inventory.commons.confirmCancel'),
            buttonText: t('buttons.delete'),
            buttonType: 'danger',
            requireComment: true,
            onConfirmation: async ({ comment }) => {
                const result = await InventoryOperationService.CancelPending(move.id, move.confirmationUserId, comment)
                if (result.Succeeded) {
                    Success(t('commons.deleteSuccess'))
                    succeededCallback()
                }
                else
                    ErrorMessage(t('commons.deleteError'))
            }
        })
    }

    const actions = {
        saveDispenseAsPeding: saveDispenseAsPeding,
        confirmDispense: confirmDispense,
        cancelDispenseOperation: cancelDispenseOperation
    }

    return [modal, actions, isWorking]
}

function getExpirationErrorKey(items, isCommentEmpty, isLaboratoryKit) {
    if (!isCommentEmpty) return null;

    const expirationThresholds = [1, 2]; // ordered from smallest to largest

    for (const months of expirationThresholds) {
        const expirationGap = new Date();
        expirationGap.setMonth(expirationGap.getMonth() + months);

        const hasItemsExpiringWithinThreshold = items.some(item => new Date(item.release.expirationDate) <= expirationGap);

        if (hasItemsExpiringWithinThreshold) {
            return getErrorMessageKey(months, isLaboratoryKit);
        }
    }

    return null;
}

function getErrorMessageKey(months, isLaboratoryKit) {
    switch (months) {
        case 1:
            return isLaboratoryKit
                ? 'inventory.dispense.errors.expirationLessThanOneMonthComment_LaboratoryKit'
                : 'inventory.dispense.errors.expirationLessThanTwoMonthsComment';
        case 2:
            return isLaboratoryKit
                ? 'inventory.dispense.errors.expirationLessThanTwoMonthsComment_LaboratoryKit'
                : 'inventory.dispense.errors.expirationLessThanTwoMonthsComment';
        default:
            return null;
    }
}